import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Card,
    Stack
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle } from 'lucide-react';
import { formatDate, getAge } from '../../helper';
import { usePatientDraftUpdateMutation, usePatientDraftApproveMutation } from './patientApi';
import { getSecureItem } from "../../utils/secureStorage";

// Normalizes a mobile number coming from any common format:
// "+33 6 12 34 56 78", "0033612345678", "06.12.34.56.78", "06-12-34-56-78", "0612345678", etc.
// Returns a clean 10-digit French mobile number starting with "0", or "" if it can't be normalized.
const normalizeMobileNumber = (rawPhone) => {
    if (!rawPhone) return '';

    // Strip spaces, dots and dashes
    let phone = String(rawPhone).replace(/[\s.-]/g, '');

    if (phone.startsWith('+33')) {
        phone = '0' + phone.substring(3);
    } else if (phone.startsWith('0033')) {
        phone = '0' + phone.substring(4);
    }
    // If it already starts with "0", it's left as is and validated below.

    return /^0\d{9}$/.test(phone) ? phone : '';
};

const AddDraftPatient = ({ setLoading, show, setShow, editData, onApprove }) => {
    const { t } = useTranslation();
    const user = getSecureItem('UserData');
    const maxDateValidation = new Date().toISOString().split('T')[0];

    const config = {
        email: '',
        password: 'SymInfo@#2024',
        mobile: '',
        userType: 'PATIENT',
        languageId: 1,
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        doYouWearGlasses: true,
        whenIsTheLastTimeYouWentToTheEyeDoctor: '',
        patientNameOfYourUsualEyeDoctor: `${user?.name} ${user?.lastName}`,
        patientRppsNumberOfYourUsualEyeDoctor: 0,
        phoneNumber: '',
        typeOfVision: '',
        patientHappy: '',
        patientSymptoms: '',
        patientDiagnosedWith: '',
        patientHealthConditions: '',
        additionalComments: '',
        doctorRppsNumber: '',
        doctorIdOfRegistration: '',
        doctorAddressOfTheHealthCenter: '',
        city: '',
        gender: '',
        age: '',
        SubmitTrack: 'Web',
        infoSSN: '',
        infoPrescriptionToOptician: true,
        isDraft: true
    };

    const [modalLoading, setModalLoading] = useState(false);
    const [error, setError] = useState('');
    const [validated, setValidated] = useState(false);
    const [addPatientConfig, setAddPatientConfig] = useState(config);
    const [patientDraftUpdate] = usePatientDraftUpdateMutation();
    const [patientDraftApprove] = usePatientDraftApproveMutation();
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertVariant, setAlertVariant] = useState('success');
    const [ageError, setAgeError] = useState('');

    useEffect(() => {
        setError('');
        setAgeError('');

        if (Object.entries(editData).length > 0) {
            const formattedDateOfBirth = formatDate(editData.dateOfBirth);
            const age = getAge(formattedDateOfBirth);

            setAddPatientConfig({
                ...editData,
                dateOfBirth: formattedDateOfBirth,
                age: age.toString()
            });
        } else {
            setAddPatientConfig(config);
        }

        setValidated(false);
        setShowAlert(false);
    }, [show]);

    // Function to calculate age from date of birth
    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return 0;

        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        return age;
    };

    // Function to validate if patient is 16+ years old
    const validateAge = (dateOfBirth) => {
        const age = calculateAge(dateOfBirth);
        return age >= 16;
    };

    const handleChange = (e) => {
        e.preventDefault();
        const { name, value } = e.target;
        setAddPatientConfig((prevValues) => {
            const updatedValues = { ...prevValues, [name]: value };

            if (name === 'dateOfBirth') {
                if (value) {
                    let ageValue = calculateAge(value);
                    updatedValues.age = ageValue.toString();

                    // Clear age error when date changes
                    setAgeError('');
                } else {
                    updatedValues.age = '';
                }
            }

            if (name === 'doYouWearGlasses') {
                updatedValues.doYouWearGlasses = value === 'true';
            }

            return updatedValues;
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;

        // Validate age before submission
        if (addPatientConfig.dateOfBirth) {
            const isValidAge = validateAge(addPatientConfig.dateOfBirth);
            if (!isValidAge) {
                setAgeError(t('patientMustBe16YearsOrOlder'));
                setValidated(true);
                return;
            }
        }

        if (form.checkValidity()) {
            setModalLoading(true);
            let result;

            if (Object.entries(editData).length > 0) {
                result = await patientDraftUpdate({
                    ...addPatientConfig,
                    isDraft: true
                });
            } else {
                result = await patientDraftUpdate({
                    ...addPatientConfig,
                    isDraft: true
                });
            }

            if (result.data) {
                setAlertMessage(
                    Object.entries(editData).length > 0
                        ? t('draftUpdatedSuccessfully')
                        : t('draftCreatedSuccessfully')
                );
                setAlertVariant('success');
                setShowAlert(true);

                setTimeout(() => {
                    setShow(false);
                    setLoading && setLoading(true);
                }, 1500);
            } else {
                setError(result.error?.message || t('somethingWentWrong'));
                setAlertMessage(result.error?.message || t('somethingWentWrong'));
                setAlertVariant('danger');
                setShowAlert(true);
            }
            setModalLoading(false);
        }
        setValidated(true);
    };

    const handleApprove = async () => {
        if (!addPatientConfig.gender) {
            setAlertMessage(t('genderRequiredForApproval') || 'Gender is required for approval');
            setAlertVariant('danger');
            setShowAlert(true);
            return;
        }
        if (!addPatientConfig.phoneNumber) {
            setAlertMessage(t('phonenumberRequiredForApproval') || 'Phone number is required for approval');
            setAlertVariant('danger');
            setShowAlert(true);
            return;
        }
        // Validate age before approval
        if (addPatientConfig.dateOfBirth) {
            const isValidAge = validateAge(addPatientConfig.dateOfBirth);
            if (!isValidAge) {
                setAgeError(t('patientMustBe16YearsOrOlderToApprove'));
                setAlertMessage(t('patientMustBe16YearsOrOlderToApprove'));
                setAlertVariant('danger');
                setShowAlert(true);
                return;
            }
        } else {
            setAgeError(t('dateOfBirthRequiredForApproval'));
            setAlertMessage(t('dateOfBirthRequiredForApproval'));
            setAlertVariant('danger');
            setShowAlert(true);
            return;
        }

        // Normalize the mobile number once, handling +33, 0033, spaces/dots/dashes, and plain 0X formats
        const normalizedMobile = normalizeMobileNumber(
            addPatientConfig.phoneNumber || addPatientConfig.mobile
        );

        // Prepare the data in the exact format expected by the approve API
        const approveData = [{
            userId: addPatientConfig.userId,
            email: addPatientConfig.email,
            password: "TempPassword123!",
            mobile: normalizedMobile,
            userType: "PATIENT",
            languageId: 1,
            name: addPatientConfig.firstName || addPatientConfig.name,
            lastName: addPatientConfig.lastName,
            dateOfBirth: addPatientConfig.dateOfBirth,
            age: addPatientConfig.age,
            doYouWearGlasses: addPatientConfig.doYouWearGlasses || false,
            patientNameOfYourUsualEyeDoctor: addPatientConfig.patientNameOfYourUsualEyeDoctor || '',
            phoneNumber: normalizedMobile,
            typeOfVision: addPatientConfig.typeOfVision || '',
            additionalComments: addPatientConfig.additionalComments || '',
            gender: addPatientConfig.gender || '',
            address: addPatientConfig.doctorAddressOfTheHealthCenter || '',
            isExternal: false,
            infoIsExternalDoctor: false,
            infoPrescriptionToOptician: false,
            isDoctorAvailable: false,
            imageRetino: false,
            isPio: false,
            isContactLense: false
        }];

        setModalLoading(true);
        const result = await patientDraftApprove(approveData);

        if (result.error.data == 'success') {
            setAlertMessage(t('patientApprovedSuccessfully'));
            setAlertVariant('success');
            setShowAlert(true);

            if (onApprove) {
                onApprove(addPatientConfig);
            }

            setTimeout(() => {
                setShow(false);
                setLoading && setLoading(true);
            }, 1500);
        } else {

            setError(result.error?.message || t('failedToApprovePatient'));
            setAlertMessage(result.error?.message || t('failedToApprovePatient'));
            setAlertVariant('danger');
            setShowAlert(true);
        }
        setModalLoading(false);
    };

    const handleClose = () => {
        setAddPatientConfig(config);
        setError('');
        setAgeError('');
        setShow(false);
    };

    return (
        <Modal
            show={show}
            size="lg"
            onHide={handleClose}
            aria-labelledby="contained-modal-title-vcenter"
            backdrop="static"
            keyboard={false}
            centered
        >
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {Object.entries(editData).length > 0
                            ? t('editDraftPatient')
                            : t('addDraftPatient')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="modal_form">
                    {showAlert && (
                        <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
                            {alertMessage}
                        </Alert>
                    )}

                    <Card className="mb-3">
                        <Card.Header>{t('personalInformation')}</Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('firstName')}</Form.Label>
                                        <Form.Control
                                            onChange={handleChange}
                                            value={addPatientConfig.firstName || addPatientConfig.name}
                                            type="text"
                                            name="firstName"
                                            placeholder={t('inputText')}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('surName')}</Form.Label>
                                        <Form.Control
                                            onChange={handleChange}
                                            value={addPatientConfig.lastName}
                                            type="text"
                                            name="lastName"
                                            placeholder={t('inputText')}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('dateofBirth')}</Form.Label>
                                        <Form.Control
                                            onChange={handleChange}
                                            max={maxDateValidation}
                                            value={addPatientConfig.dateOfBirth}
                                            type="date"
                                            name="dateOfBirth"
                                            placeholder={t('inputText')}
                                            isInvalid={!!ageError}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {ageError}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('age')}</Form.Label>
                                        <Form.Control
                                            value={addPatientConfig.age || ''}
                                            type="text"
                                            name="age"
                                            placeholder={t('inputText')}
                                            disabled
                                        />
                                        {addPatientConfig.age && (
                                            <Form.Text className={validateAge(addPatientConfig.dateOfBirth) ? "text-success" : "text-danger"}>
                                                {validateAge(addPatientConfig.dateOfBirth)
                                                    ? t("âge éligible pour l'approbation")
                                                    : t("âge non éligible pour l'approbation")}
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('email')}*</Form.Label>
                                        <Form.Control
                                            onChange={handleChange}
                                            value={addPatientConfig.email}
                                            type="email"
                                            name="email"
                                            placeholder={t('inputText')}
                                            required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {t('emailRequired')}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('phone')}*</Form.Label>
                                        <Form.Control
                                            onChange={handleChange}
                                            value={addPatientConfig.phoneNumber || addPatientConfig.mobile}
                                            type="tel"
                                            name="phoneNumber"
                                            placeholder={t('inputText')}
                                            isInvalid={showAlert && !addPatientConfig.phoneNumber}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {t('phonenumberRequiredForApproval')}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('gender')}*</Form.Label>
                                        <Form.Select
                                            aria-label="Default"
                                            name="gender"
                                            onChange={handleChange}
                                            value={addPatientConfig.gender}
                                            isInvalid={showAlert && !addPatientConfig.gender}
                                        >
                                            <option value={''}>{t('selectItem')}</option>
                                            <option value="Male">{t('male')}</option>
                                            <option value="Female">{t('female')}</option>
                                            <option value="Others">{t('others')}</option>
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {t('genderRequiredForApproval')}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('ssnNumber')}</Form.Label>
                                        <Form.Control
                                            onChange={handleChange}
                                            value={addPatientConfig.infoSSN}
                                            name="infoSSN"
                                            placeholder={t('inputText')}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>{t('address')}</Form.Label>
                                <Form.Control
                                    onChange={handleChange}
                                    value={addPatientConfig.doctorAddressOfTheHealthCenter}
                                    as="textarea"
                                    name="doctorAddressOfTheHealthCenter"
                                    rows={2}
                                    type="text"
                                    placeholder={t('inputText')}
                                />
                            </Form.Group>
                        </Card.Body>
                    </Card>
                    {/*                     
                    <Card className="mb-3">
                        <Card.Header>{t('eyeCareInformation')}</Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('typeofVision')}</Form.Label>
                                        <Form.Select
                                            aria-label="Default"
                                            name="typeOfVision"
                                            onChange={handleChange}
                                            value={addPatientConfig.typeOfVision}
                                        >
                                            <option value={''}>{t('selectItem')}</option>
                                            <option value="Far Vision">{t('farVision')}</option>
                                            <option value="Close Vision">{t('closeVision')}</option>
                                            <option value="Progressive Vision">{t('progressiveVision')}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('doesSubjectWearGlasses')}</Form.Label>
                                        <Form.Select
                                            aria-label="Default"
                                            name="doYouWearGlasses"
                                            onChange={handleChange}
                                            value={addPatientConfig.doYouWearGlasses}
                                        >
                                            <option value={''}>{t('selectItem')}</option>
                                            <option value={true}>{t('yes')}</option>
                                            <option value={false}>{t('no')}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>{t('usualEyeDoctorName')}</Form.Label>
                                <Form.Control
                                    onChange={handleChange}
                                    value={addPatientConfig.patientNameOfYourUsualEyeDoctor}
                                    type="text"
                                    name="patientNameOfYourUsualEyeDoctor"
                                    placeholder={t('inputText')}
                                />
                            </Form.Group>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>{t('patientAdditionalComments')}</Form.Label>
                                <Form.Control
                                    onChange={handleChange}
                                    value={addPatientConfig.additionalComments}
                                    as="textarea"
                                    name="additionalComments"
                                    rows={3}
                                    type="text"
                                    placeholder={t('inputText')}
                                />
                            </Form.Group>
                        </Card.Body>
                    </Card>
                     */}
                    {error !== '' && (
                        <Alert variant={'danger'}>{error}</Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Stack direction="horizontal" gap={2} className="w-100">
                        {Object.entries(editData).length > 0 && (
                            <Button
                                variant="success"
                                onClick={handleApprove}
                                className="me-auto"
                                disabled={modalLoading || !validateAge(addPatientConfig.dateOfBirth)}
                                title={!validateAge(addPatientConfig.dateOfBirth) ? t('patientMustBe16YearsOrOlderToApprove') : ''}
                            >
                                <CheckCircle width={16} className="me-1" />
                                {t('approvePatient')}
                            </Button>
                        )}
                        <Button variant="secondary" onClick={handleClose}>
                            <X width={16} className="me-1" />
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={modalLoading}
                        >
                            {modalLoading && (
                                <Spinner
                                    animation="border"
                                    role="status"
                                    size="sm"
                                >
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                            )}{' '}
                            {Object.entries(editData).length > 0
                                ? t('updateDraft')
                                : t('saveDraft')}
                        </Button>
                    </Stack>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};
export default AddDraftPatient;
